'use client';

import clsx from 'clsx';
import { PropsWithChildren, useEffect, useState } from 'react';

import Spinner from '../Spinner';
import { ButtonProps, ButtonSize, ButtonColor, ButtonVariant } from './types';
import styles from './Button.module.css';

const Button = ({
  id,
  classes,
  loading,
  fullWidth,
  largeButton,
  size = 'large',
  color = 'primary',
  variant = 'contained',
  type = 'button',
  startIcon,
  startIconClassName,
  endIcon,
  endIconClassName,
  label,
  children,
  form,
  disabled,
  ariaLabel,
  onClick,
  disableLoading,
  ...rest
}: PropsWithChildren<ButtonProps>) => {
  const [rippleState, setRippleState] = useState<any>({ left: 0, top: 0 });
  const mapButtonSize = (buttonSize: ButtonSize): string => {
    const sizes: Record<string, string> = {
      large: 'btn_size_large',
      medium: 'btn_size_medium',
      small: 'btn_size_small',
    };
    return sizes[buttonSize];
  };

  const mapButtonColor = (buttonColor: ButtonColor): string => {
    const colors: Record<string, string> = {
      inherit: styles.btn_color_inherit,
      primary: styles.btn_color_primary,
      secondary: styles.btn_color_secondary,
    };
    return colors[buttonColor] ? colors[buttonColor] : (buttonColor as unknown as string);
  };

  const mapButtonVariant = (buttonVariant: ButtonVariant): string => {
    const variants: Record<string, string> = {
      text: `${styles.btn_variant_text}`,
      contained: 'bg-red btn-contained',
      outlined: 'bg-transparent btn-outlined',
      secondary: `${styles.btn_variant_secondary}`,
    };
    return variants[buttonVariant];
  };

  const combinedClassName = clsx(
    { [styles.full]: fullWidth },
    { [styles.large]: largeButton },
    styles.btn_root,
    styles.btn_root_transition,
    mapButtonSize(size),
    mapButtonColor(color as ButtonColor),
    mapButtonVariant(variant),
    classes,
  );
  const isDisabled: boolean = loading !== undefined && loading;

  let timer: any = 0;
  const runRipple = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    ev.stopPropagation();

    const { pageX, pageY, currentTarget } = ev;
    const rect = currentTarget.getBoundingClientRect();

    const left = pageX - (rect.left + window.scrollX) || 0;
    const top = pageY - (rect.top + window.scrollY) || 0;
    const rippleSize = Math.max(rect.width, rect.height);

    setRippleState((prevState: any) => ({
      ...prevState,
      left,
      top,
      opacity: 0.3,
      transform: 'translate(-50%, -50%)',
      transition: 'cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: '#9e9e9e',
    }));
    timer = setTimeout(() => {
      setRippleState((prevState: any) => ({
        ...prevState,
        opacity: 0,
        transform: `scale(${rippleSize / 9})`,
        transition: `all ${1000}ms`,
      }));
    }, 10);
  };

  useEffect(() => {
    return clearTimeout(timer);
  }, []);

  return (
    <button
      id={id}
      data-testid={id}
      aria-label={ariaLabel || 'button'}
      onClick={(event) => {
        if (onClick) {
          onClick(event);
        }
        runRipple(event);
      }}
      disabled={isDisabled || disabled}
      className={combinedClassName}
      type={type}
      form={form}
      {...rest}
    >
      {startIcon && <div className={startIconClassName}>{startIcon}</div>}
      {typeof label === 'string' ? <span className={styles.labelDescription}>{label}</span> : label}
      {children}
      {loading !== undefined && loading && !disableLoading ? (
        <Spinner className={styles.buttonLoading} />
      ) : (
        <span className={endIconClassName}>{endIcon}</span>
      )}
      {!disabled && <span className={styles.rippleRoot} style={{ ...rippleState }}></span>}
    </button>
  );
};

export default Button;
